import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Company, FundingRecord } from '../lib/api'
import { useState } from 'react'
import { Wallet, Plus, CheckCircle, Clock, XCircle } from 'lucide-react'
import clsx from 'clsx'

const formatCurrency = (pence: number) => 
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100)

const statusIcons: Record<string, typeof CheckCircle> = {
  completed: CheckCircle,
  pending: Clock,
  failed: XCircle
}

const statusColors: Record<string, string> = {
  completed: 'text-green-600',
  pending: 'text-amber-600',
  failed: 'text-red-600'
}

export default function Funding() {
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState('')
  const [amount, setAmount] = useState('')

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get<Company[]>('/companies')
  })

  const { data: funding, isLoading } = useQuery({
    queryKey: ['funding'],
    queryFn: () => api.get<(FundingRecord & { company_name: string })[]>('/funding')
  })

  const addFunding = useMutation({
    mutationFn: (data: { company_id: string; amount: number }) =>
      api.post('/funding', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding'] })
      setShowAdd(false)
      setSelectedCompany('')
      setAmount('')
    }
  })

  const handleSubmit = () => {
    const amountPence = Math.round(parseFloat(amount) * 100)
    if (selectedCompany && amountPence > 0) {
      addFunding.mutate({ company_id: selectedCompany, amount: amountPence })
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Funding</h1>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-salad-600 text-white px-4 py-2 rounded-lg hover:bg-salad-700"
        >
          <Plus size={20} />
          Add Funding
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Add Company Funding</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">Select Company</option>
              {companies?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500">£</span>
              <input
                type="number"
                placeholder="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-2 border rounded-lg"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSubmit}
              disabled={!selectedCompany || !amount || addFunding.isPending}
              className="bg-salad-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {addFunding.isPending ? 'Processing...' : 'Add Funding'}
            </button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-gray-600">
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="animate-pulse space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-16 bg-gray-200 rounded-lg" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {funding?.map(record => {
            const StatusIcon = statusIcons[record.status] || Clock
            return (
              <div key={record.id} className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-4">
                <div className="w-12 h-12 bg-salad-100 rounded-lg flex items-center justify-center">
                  <Wallet className="text-salad-600" size={24} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{record.company_name}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(record.created_at).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIcon className={clsx(statusColors[record.status])} size={18} />
                  <span className="text-sm text-gray-500">{record.status}</span>
                </div>
                <div className="text-xl font-bold text-salad-600">
                  {formatCurrency(record.amount)}
                </div>
              </div>
            )
          })}
          {funding?.length === 0 && (
            <div className="text-center py-12 text-gray-500">No funding records yet</div>
          )}
        </div>
      )}
    </div>
  )
}
